import { Component, OnDestroy, OnInit, computed, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  StatusBarComponent,
  HeroSectionComponent,
} from '@live-pickup/presentation';
import { AccessPointOption, LivePickupStore } from '@live-pickup/data-access';
import { AccessPointEdgeStore, SettingsStore } from '@settings/data-access';
import { UiStore } from '@theme/data-access';

@Component({
  selector: 'lib-live-pickup-shell',
  standalone: true,
  imports: [RouterModule, StatusBarComponent, HeroSectionComponent],
  templateUrl: './live-pickup-shell.component.html',
  styleUrl: './live-pickup-shell.component.scss',
})
export class LivePickupShellComponent implements OnInit, OnDestroy {
  readonly store = inject(LivePickupStore);
  private readonly edgeStore = inject(AccessPointEdgeStore);
  private readonly settingsStore = inject(SettingsStore);
  private readonly uiStore = inject(UiStore);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly isMobile = this.uiStore.isMobile;
  readonly hideHeroOnMobile = computed(
    () => this.isMobile() && !this.store.heroStudent(),
  );

  readonly gateOptions = computed<AccessPointOption[]>(() => {
    const edgeById = new Map(
      Object.values(this.edgeStore.edgesEntities()).map(
        (edge) => [edge.EdgeId, edge] as const,
      ),
    );
    const qualityById = new Map(
      Object.values(this.edgeStore.accessPointsEntities()).map((ap) => {
        const edge = ap.EdgeId ? edgeById.get(ap.EdgeId) : undefined;
        return [
          ap.AccessPointId,
          edge?.SignalQuality ?? ap.SignalQuality ?? null,
        ] as const;
      }),
    );
    return this.store
      .accessPoints()
      .map((ap) => ({ ...ap, signalQuality: qualityById.get(ap.id) ?? null }));
  });

  ngOnInit(): void {
    if (!this.settingsStore.ownerSettings()) {
      this.settingsStore.loadOwnerSettings();
    }
    this.edgeStore.startHub().catch((error) => {
      console.warn('Hub connection failed:', error);
    });
    if (!this.edgeStore.loadInitialDevicesLoaded()) {
      this.edgeStore.loadInitialDevices();
    }
    if (
      !this.edgeStore.loadAccessPointsLoaded() &&
      !this.edgeStore.loadAccessPointsLoading()
    ) {
      this.edgeStore.loadAccessPoints();
    }
  }

  ngOnDestroy(): void {
    this.edgeStore.stopHub();
  }

  onAccessPointChange(option: AccessPointOption | null): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        accessPointId: option?.id ?? null,
        accessPointName: option?.name ?? null,
      },
      queryParamsHandling: 'merge',
    });
    this.store.switchAccessPoint(option?.id, option?.name);
  }
}
